/**
 * V2-069 – which Work ID pins the map frames on load.
 *
 * Work IDs are in British Columbia and along the Alberta border. A pin outside that region is a
 * bad coordinate (0,0, a flipped sign, a typo'd degree), and fitting to it leaves the BC pins in
 * one corner of a map of half of Canada. Such pins are still drawn – they are only left out of
 * the initial frame. When every pin is outside the region, all of them are framed.
 */
export interface LatLng {
  lat: number;
  lng: number;
}

/** BC plus a margin over the Alberta border (lat 48–60.5 N, lng 139.5–109 W). */
export const SERVICE_REGION = { south: 48, north: 60.5, west: -139.5, east: -109 } as const;

export function isInServiceRegion(p: LatLng): boolean {
  return (
    p.lat >= SERVICE_REGION.south &&
    p.lat <= SERVICE_REGION.north &&
    p.lng >= SERVICE_REGION.west &&
    p.lng <= SERVICE_REGION.east
  );
}

export function pointsToFit(points: LatLng[]): LatLng[] {
  const valid = points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  const inRegion = valid.filter(isInServiceRegion);
  return inRegion.length > 0 ? inRegion : valid;
}

/**
 * V3-078: the pins left out of the frame (bad coordinates), for the build notes and support. The
 * caller passes whatever identifies a pin; only valid coordinates outside the region are returned.
 */
export function pinsOutsideServiceRegion<T extends LatLng>(points: T[]): T[] {
  return points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && !isInServiceRegion(p));
}

/**
 * V3-078: a map box this small has not been laid out yet. fitBounds on it chooses a zoom for a
 * sliver (or for 0 px, the whole world) and the map keeps that zoom when the box grows.
 */
export const MIN_FIT_SIZE_PX = 100;

export function hasRenderableSize(width: number, height: number): boolean {
  return width >= MIN_FIT_SIZE_PX && height >= MIN_FIT_SIZE_PX;
}
